import { useState, useEffect, useRef } from 'react';

/**
 * Module-level lock counter so multiple overlays opening at once stay
 * correctly locked and unlocked without clobbering each other.
 */
let _scrollLockCount = 0;
let _scrollLockSavedY = 0;
let _scrollLockSavedStyles = /** @type {Record<string,string>} */ ({});

/**
 * Locks body scroll while `active` is true. Stack-safe: the first caller
 * saves the pre-lock body styles and scrollY; subsequent callers increment the
 * counter without re-saving. The page scroll is restored only when the last
 * active lock is released. Preserves any inline body styles that were already
 * set before the lock (rather than blindly writing empty strings on cleanup).
 */
export function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const body = document.body;

    if (_scrollLockCount === 0) {
      // Save current state before modifying
      _scrollLockSavedY = window.scrollY;
      _scrollLockSavedStyles = {
        position:  body.style.position,
        top:       body.style.top,
        left:      body.style.left,
        right:     body.style.right,
        overflowY: body.style.overflowY,
      };
      body.style.position  = 'fixed';
      body.style.top       = `-${_scrollLockSavedY}px`;
      body.style.left      = '0';
      body.style.right     = '0';
      body.style.overflowY = 'scroll'; // preserve scrollbar width → no layout shift
    }
    _scrollLockCount++;

    return () => {
      _scrollLockCount = Math.max(0, _scrollLockCount - 1);
      if (_scrollLockCount === 0) {
        // Restore exactly what was there before
        body.style.position  = _scrollLockSavedStyles.position;
        body.style.top       = _scrollLockSavedStyles.top;
        body.style.left      = _scrollLockSavedStyles.left;
        body.style.right     = _scrollLockSavedStyles.right;
        body.style.overflowY = _scrollLockSavedStyles.overflowY;
        window.scrollTo(0, _scrollLockSavedY);
      }
    };
  }, [active]);
}

// Probes /health to detect real connectivity (navigator.onLine is unreliable
// with Chrome DevTools throttling and doesn't detect the Pi being unreachable).
// Returns: null = still checking, true = reachable, false = unreachable.
export function useConnectivity() {
  const [online, setOnline] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const probe = () => {
      fetch('/health', { cache: 'no-store' })
        .then(r => { if (!cancelled) setOnline(r.ok); })
        .catch(() => { if (!cancelled) setOnline(false); });
    };

    probe();

    const handleOnline  = () => probe();
    const handleOffline = () => { if (!cancelled) setOnline(false); };
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      cancelled = true;
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}

export function usePullToRefresh(onRefresh) {
  const [refreshing, setRefreshing] = useState(false);
  const activeRef = useRef(false);

  useEffect(() => {
    let startY = null;

    const onTouchStart = (e) => {
      const scrollEl = document.querySelector('.app-scroll-area');
      if (!scrollEl || scrollEl.scrollTop > 0) return;
      startY = e.touches[0].clientY;
    };

    const onTouchEnd = async (e) => {
      if (startY === null || activeRef.current) return;
      const dy = e.changedTouches[0].clientY - startY;
      startY = null;
      if (dy > 72) {
        activeRef.current = true;
        setRefreshing(true);
        await onRefresh();
        setRefreshing(false);
        activeRef.current = false;
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onRefresh]);

  return refreshing;
}
